import { Bot, CalendarDays, UserRound, UsersRound } from 'lucide-react'
import { clsx } from 'clsx'
import type { SearchAuthorFilter, SearchFilters } from '../lib/api'

export type SearchDateMode = 'any' | 'today' | '7d' | '30d' | 'range'

export type SearchFilterState = {
  author: SearchAuthorFilter
  dateMode: SearchDateMode
  fromDate: string
  toDate: string
}

const DAY_SECONDS = 24 * 60 * 60

const AUTHOR_OPTIONS: Array<{
  value: SearchAuthorFilter
  label: string
  Icon: typeof UsersRound
}> = [
  { value: 'all', label: 'All', Icon: UsersRound },
  { value: 'me', label: 'Me', Icon: UserRound },
  { value: 'agentchat', label: 'AgentChat', Icon: Bot },
]

const DATE_OPTIONS: Array<{ value: SearchDateMode; label: string }> = [
  { value: 'any', label: 'Any time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'range', label: 'Range' },
]

export function defaultSearchFilterState(): SearchFilterState {
  return {
    author: 'all',
    dateMode: 'any',
    fromDate: '',
    toDate: '',
  }
}

function startOfLocalDaySeconds(date = new Date()): number {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return Math.floor(d.getTime() / 1000)
}

function dateInputToLocalStartSeconds(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day, 0, 0, 0, 0)
  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return null
  }
  return Math.floor(date.getTime() / 1000)
}

export function searchFilterParams(filters: SearchFilterState): SearchFilters {
  const todayStart = startOfLocalDaySeconds()
  const todayEnd = todayStart + DAY_SECONDS - 1
  let fromTs: number | null = null
  let toTs: number | null = null

  if (filters.dateMode === 'today') {
    fromTs = todayStart
    toTs = todayEnd
  } else if (filters.dateMode === '7d') {
    fromTs = todayStart - 6 * DAY_SECONDS
    toTs = todayEnd
  } else if (filters.dateMode === '30d') {
    fromTs = todayStart - 29 * DAY_SECONDS
    toTs = todayEnd
  } else if (filters.dateMode === 'range') {
    const fromStart = dateInputToLocalStartSeconds(filters.fromDate)
    const toStart = dateInputToLocalStartSeconds(filters.toDate)
    fromTs = fromStart
    toTs = toStart === null ? null : toStart + DAY_SECONDS - 1
  }

  return {
    author: filters.author,
    fromTs,
    toTs,
  }
}

type SearchFilterBarProps = {
  filters: SearchFilterState
  onChange: (filters: SearchFilterState) => void
  className?: string
}

export default function SearchFilterBar({
  filters,
  onChange,
  className,
}: SearchFilterBarProps) {
  const update = (patch: Partial<SearchFilterState>) => {
    onChange({ ...filters, ...patch })
  }

  return (
    <div className={clsx('flex flex-wrap items-center gap-2 text-xs', className)}>
      <div className="inline-grid h-9 grid-flow-col auto-cols-max gap-1 rounded-lg bg-stone-100 p-1 dark:bg-stone-800">
        {AUTHOR_OPTIONS.map(({ value, label, Icon }) => {
          const active = filters.author === value
          return (
            <button
              key={value}
              type="button"
              onClick={() => update({ author: value })}
              aria-pressed={active}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-md px-2 text-xs font-medium whitespace-nowrap',
                active
                  ? 'bg-white text-stone-950 shadow-sm dark:bg-stone-700 dark:text-stone-50'
                  : 'text-stone-500 hover:text-stone-800 dark:hover:text-stone-200',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              <span>{label}</span>
            </button>
          )
        })}
      </div>

      <label className="inline-flex h-9 min-w-0 items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2 dark:border-stone-700 dark:bg-stone-800">
        <CalendarDays className="h-3.5 w-3.5 shrink-0 text-stone-400" aria-hidden />
        <select
          value={filters.dateMode}
          onChange={(e) => update({ dateMode: e.target.value as SearchDateMode })}
          aria-label="Date filter"
          className="min-w-[7rem] bg-transparent text-xs font-medium text-stone-700 outline-none dark:text-stone-200"
        >
          {DATE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>

      {filters.dateMode === 'range' && (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <label className="inline-flex h-9 min-w-[8.75rem] items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2 dark:border-stone-700 dark:bg-stone-800">
            <span className="shrink-0 text-[11px] font-medium text-stone-500">From</span>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => update({ fromDate: e.target.value })}
              className="min-w-0 flex-1 bg-transparent text-xs text-stone-800 outline-none dark:text-stone-100"
            />
          </label>
          <label className="inline-flex h-9 min-w-[8.75rem] items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2 dark:border-stone-700 dark:bg-stone-800">
            <span className="shrink-0 text-[11px] font-medium text-stone-500">To</span>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => update({ toDate: e.target.value })}
              className="min-w-0 flex-1 bg-transparent text-xs text-stone-800 outline-none dark:text-stone-100"
            />
          </label>
        </div>
      )}
    </div>
  )
}
